import React, { useState, useCallback } from 'react'
import { FileText } from 'lucide-react'
import { handleValidationErrors, formatPhone } from '@/shared/utils'
import { validateRobloxLegalVerification } from '../validation/robloxLegalVerificationValidation'
import type { RobloxLegalVerificationProps, ValidationErrors } from '@/features/enrollment'
import ValidationErrorDisplay from '../../shared/ValidationErrorDisplay'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'

export default function Step3_LegalVerification({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  handleSimpleNavigate,
  handleNavigateWithCompletion,
  isNavigating,
  isCompleting,
  sessionId,
  currentStepIndex,
  totalSteps = 10
}: RobloxLegalVerificationProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})

  const handleNext = useCallback(async (): Promise<boolean> => {
    const validationErrors = validateRobloxLegalVerification(enrollmentData)
    setErrors(validationErrors)

    const isValid = Object.keys(validationErrors).length === 0

    if (!isValid) {
      handleValidationErrors(validationErrors)
      return false
    }

    return true
  }, [enrollmentData])

  const handleBack = useCallback(() => {
    setErrors({})
  }, [])

  const getValidationErrors = (): string[] => {
    return Object.values(errors).filter(Boolean)
  }

  return (
    <StageWrapper
      currentStepIndex={currentStepIndex ?? 0}
      totalSteps={totalSteps}
      handleNavigate={handleNavigate}
      handleNavigateWithCompletion={handleNavigateWithCompletion}
      onNext={handleNext}
      onBack={handleBack}
      isNavigating={isNavigating}
      isCompleting={isCompleting}
    >
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Legal Verification & Documentation</h3>
            <p className="text-sm text-accent-600">Provide required verification and contact information.</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Emergency Contact Information */}
          <div>
            <h4 className="text-md font-semibold text-accent-800 mb-4">Emergency Contact Information</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What would be an alternative contact in case we are unable to reach you? *
              </label>
              <select
                value={enrollmentData.alternativeContact || ''}
                onChange={(e) => updateEnrollmentData('alternativeContact', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.alternativeContact ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select an option</option>
                <option value="Agreed to provide">Agreed to provide</option>
                <option value="Does not have an alternative contact">Does not have an alternative contact</option>
                <option value="Does not wish to provide the information">Does not wish to provide the information</option>
              </select>
              {errors.alternativeContact && (
                <p className="mt-1 text-sm text-red-600">{errors.alternativeContact}</p>
              )}
            </div>

            {/* Additional fields for alternative contact */}
            {enrollmentData.alternativeContact === 'Agreed to provide' && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alternative Contact First Name *
                    </label>
                    <input
                      type="text"
                      value={enrollmentData.alternativeContactFirstName || ''}
                      onChange={(e) => updateEnrollmentData('alternativeContactFirstName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.alternativeContactFirstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter first name"
                    />
                    {errors.alternativeContactFirstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.alternativeContactFirstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alternative Contact Last Name *
                    </label>
                    <input
                      type="text"
                      value={enrollmentData.alternativeContactLastName || ''}
                      onChange={(e) => updateEnrollmentData('alternativeContactLastName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.alternativeContactLastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter last name"
                    />
                    {errors.alternativeContactLastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.alternativeContactLastName}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alternative Contact Phone *
                    </label>
                    <input
                      type="tel"
                      pattern="^$|^\(\d{3}\) \d{3}-\d{4}$"
                      value={enrollmentData.alternativeContactPhone || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const digitsOnly = inputValue.replace(/\D/g, '');
                        if (digitsOnly.length === 0) {
                          updateEnrollmentData('alternativeContactPhone', '');
                          return;
                        }
                        const formatted = formatPhone(inputValue);
                        updateEnrollmentData('alternativeContactPhone', formatted);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.alternativeContactPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="(123) 456-7890"
                      onInvalid={(e) => {
                        e.preventDefault(); // Prevent HTML5 validation message
                      }}
                      maxLength={14}
                    />
                    {errors.alternativeContactPhone && (
                      <p className="mt-1 text-sm text-red-600">{errors.alternativeContactPhone}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Relationship to Injured Party *
                    </label>
                    <input
                      type="text"
                      value={enrollmentData.alternativeContactRelationship || ''}
                      onChange={(e) => updateEnrollmentData('alternativeContactRelationship', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.alternativeContactRelationship ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter relationship"
                    />
                    {errors.alternativeContactRelationship && (
                      <p className="mt-1 text-sm text-red-600">{errors.alternativeContactRelationship}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <h4 className="text-md font-semibold text-accent-800 mb-4">Additional Notes</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={enrollmentData.notes || ''}
                onChange={(e) => updateEnrollmentData('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter any additional notes or information"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Validation Error Display */}
      <ValidationErrorDisplay
        errors={getValidationErrors()}
        title="Please resolve the following issues to continue:"
      />
    </StageWrapper>
  )
}